import { autoinject } from "aurelia-framework";

import { IdentityClient } from "./../infrastructure/identity-client";
import { SSOType } from "../enums/sso-type";
import { IChangePasswordCommand } from "./profile-service";

@autoinject
export class ChallengeService {
  private _client: IdentityClient;

  public constructor(client: IdentityClient) {
    this._client = client;
  }

  public async getLoginContext(returnUrl: string) {
    let response = await this._client
      .build("v1/challenge/context")
      .useQueryString<IAuthorizationRequestQuery>({
        returnUrl,
      })
      .fetch<IAuthorizationRequest>(false);
    return response;
  }

   public async getCutOffDate() {
    let response = await this._client
      .build("v1/meta/cutoff-date")
      .fetch<IGetCutOffDate>();

    return response;
  }

  public async challengeEndUserCredential(
    email: string,
    password: string,
    gRecaptchaResponse: string
  ) {
    let response = await this._client
      .build("v1/challenge/credential")
      .useMethod("POST")
      .useBody<IGetEndUserByEmailAndPasswordQuery>({
        email,
        password,
      })
      .useRecaptchaValidation(gRecaptchaResponse)
      .fetchOkErrors<IGetEndUserByEmailAndPasswordResult>(false);
    return response;
  }

  public async challengeEmail(email: string) {
    let response = await this._client
      .build("v1/challenge/email")
      .useMethod("POST")
      .useBody<IGetEndUserByEmailQuery>({
        email,
      })
      .fetch<IGetEndUserByEmailResult>(false);
    return response;
  }

  public async challengeDomain(domain: string) {
    let response = await this._client
      .build("v1/challenge/domain")
      .useMethod("POST")
      .useBody<IGetRedirectionByDomainQuery>({
        domain,
      })
      .fetch<IGetEndUserByEmailResult>(false);
    return response;
  }

  public async challengeOneLogin(token: string) {
    let response = await this._client
      .build("v1/challenge/onelogin")
      .useMethod("POST")
      .useQueryString<IGetEndUserByOneLoginQuery>({
        token,
      })
      .fetch<IGetEndUserByOneLoginResult>(false);
    return response;
  }

  public async challengeAzureAd(token: string, code: string, state: string) {
    let response = await this._client
      .build("v1/challenge/azure-ad")
      .useMethod("POST")
      .useQueryString<IGetEndUserByAzureQuery>({
        token,
        code,
        state,
      })
      .fetch<IGetEndUserByAzureResult>(false);
    return response;
  }

  public async challengeOkta(code: string, state: string) {
    let response = await this._client
      .build("v1/challenge/okta")
      .useMethod("POST")
      .useQueryString<IGetEndUserByOktaQuery>({
        code,
        state,
      })
      .fetch<IGetEndUserByOneLoginResult>(false);
    return response;
  }

  public async challengeGoogleWorkspace(code: string, state: string) {
    let response = await this._client
      .build("v1/challenge/google-workspace")
      .useMethod("POST")
      .useQueryString<IGetEndUserByGoogleWorkspaceQuery>({
        code,
        state,
      })
      .fetch<IGetEndUserByAzureResult>(false);
    return response;
  }  

  public async challengeSecondFactor(code: string) {
    let response = await this._client
      .build("v1/challenge/second-factor")
      .useMethod("POST")
      .useBody<IVerifyTotpChallengeQuery>({
        code,
      })
      .fetch<IVerifyTotpChallengeResult>(false);
    return response;
  }

  public async requestDeviceChallenge() {
    let response = await this._client
      .build("v1/challenge/device/request")
      .useMethod("POST")
      .useBody({})
      .fetch<ICreateDeviceChallengeResult>();
    return response;
  }

  public async verifyDeviceChallenge(
    challengeId: string,
    challengeSecret: string
  ) {
    let response = await this._client
      .build("v1/challenge/device/verify")
      .useMethod("POST")
      .useBody<IVerifyChallengeCommand>({
        challengeId,
        challengeSecret,
      })
      .fetch<IVerifyChallengeResult>(false);
    return response;
  }

  public async checkDeviceChallenge(challengeId: string = null) {
    let response = await this._client
      .build("v1/challenge/device/check")
      .useMethod("POST")
      .useBody<ICheckChallengeCommand>({
        challengeId,
      })
      .fetch<ICheckChallengeResult>(false);
    return response;
  }

  public async selectOtpCodePreference(ChallengeType: number | string = null) {
    let response = await this._client
      .build("v1/challenge/device/otp")
      .useMethod("POST")
      .useBody({
        ChallengeType
      })
      .fetch<ISelectOtpCodePreference>();
    return response;
  }

  
  public async verifyTextCallOtpChallange(code: number | string = null) {
    let response = await this._client
      .build("v1/challenge/second-factor/otp")
      .useMethod("POST")
      .useBody({
        code
      })
      .fetch<IVerifyTextCallOtpChallange>();
    return response;
  }

  public async identityChallenge() {
    let response = await this._client
      .build("v1/challenge/identity")
      .useMethod("POST")
      .fetchNoContent(false);
    return response;
  }

  public async passwordCheckChallenge() {
    let response = await this._client
      .build("v1/challenge/password-status")
      .useMethod("GET")
      .fetch<IPasswordStatusResult>();
    return response;
  }

  public async updatePasswordChallenge(
    currentPassword: string,
    newPassword: string
  ) {
    let response = await this._client
      .build("v1/challenge/password")
      .useMethod("PUT")
      .useBody<IChangePasswordCommand>({ currentPassword, newPassword })
      .fetchAllowErrors();
    return response;
  }

   public async getTotpStatus() {
      let response = await this._client
        .build("v1/challenge/totp")
        .fetch<ITotpStatus>();
  
      return response;
    }
  
    public async getSecurityLoginOptions(){
      let response=await this._client
      .build("v1/challenge/security-key/login-options")
      .useMethod("POST")
      .fetch<{}>();

      return response;
    }

    public async verifySecurityKey(payload:any){
      let response=await this._client
      .build("v1/challenge/second-factor/security-key")
      .useMethod("POST")
      .useBody(payload)
      .fetch<{}>();
      return response;
    }
}

export interface ITotpStatus {
  active: boolean;
  enabledOn: string;
  challengeType:number;
  mobileNumber:string;
}

export interface IAuthorizationRequestQuery {
  returnUrl: string;
}

export interface IAuthorizationRequest {
  clientId: string;
  displayMode: string;
  redirectUri: string;
  uiLocales: string;
  idP: string;
  tenant: string;
  loginHint: string;
  promptMode: string;
  acrValues: string[];
  scopesRequested: string[];
  parameters: any[];
}

export interface IGetEndUserByEmailAndPasswordQuery {
  email: string;
  password: string;
}

export interface IGetEndUserByEmailAndPasswordResult {
  subjectId: string;
}

export interface IGetEndUserByEmailQuery {
  email: string;
}

export interface IGetRedirectionByDomainQuery {
  domain: string;
}

export interface IGetEndUserByEmailResult {
  ssoType: SSOType;
  redirectUrl: string;
}

export interface IGetEndUserByOneLoginQuery {
  token: string;
}

export interface IGetEndUserByAzureQuery {
  token: string;
  code: string;
  state: string;
}

export interface IGetEndUserByOktaQuery {
  code: string;
  state: string;
}

export interface IGetEndUserByGoogleWorkspaceQuery {
  code: string;
  state: string;
}

export interface IGetEndUserByOneLoginResult {
  subjectId: string;
  email: string;
}

export interface IGetEndUserByAzureResult {
  subjectId: string;
  email: string;
}

export interface IGetCutOffDate {
    cutoffDate: string;
}
export interface ISelectOtpCodePreference {
    isSuccessful: string;
    message: string;
    statusCode?: number;
}

export interface IVerifyTextCallOtpChallange {
    required: boolean;
    valid: boolean;
    challengeId?: number;
}
export interface IVerifyTotpChallengeQuery {
  code: string;
}

export interface IVerifyTotpChallengeResult {
  required: boolean;
  valid: boolean;
  challengeId: string;
}

export interface ICreateDeviceChallengeResult {
  challengeId: string;
  expires: string;
}

export interface IVerifyChallengeCommand {
  challengeId: string;
  challengeSecret: string;
}

export interface IVerifyChallengeResult {
  challengeId: string;
}

export interface ICheckChallengeCommand {
  challengeId: string;
}

export interface ICheckChallengeResult {
  challengeId: string;
}

export interface IPasswordStatusResult {
  endOfGracePeriod: string;
  isPasswordChangeRequired: boolean;
}
